<template>
  <div
    :class="['left-container', { 'is-closed': !isOpen }]"
    :style="sidebarStyles"
  >
    <!-- Logo section -->
    <div class="logo-section">
      <router-link :to="{ name: 'main' }">
        <div class="logo-wrapper">
          <v-icon
            v-if="isOpen"
            name="qbeek-logo"
            width="136"
            height="40"
            stroke="none"
            action-color="transparent"
          />
          <div
            v-else
            class="logo-compact"
          >
            <v-icon
              name="qbeek-logo-small"
              width="54"
              height="40"
              stroke="none"
              action-color="transparent"
            />
          </div>
        </div>
      </router-link>
    </div>

    <!-- Main navigation -->
    <div class="nav-section">
      <!-- NavMenuList for ListSideMenu items -->
      <NavMenuList
        :items="ListSideMenu"
        :is-open="isOpen"
        :user-permissions="userPermissions"
        @item-click="handleListMenuClick"
      />
    </div>

    <!-- Toggle button -->
    <button
      class="toggle-button"
      @click="toggleSidebar"
    >
      <v-icon
        name="carret"
        :rotate="90"
        :rotate180="!isOpen"
        width="20"
        height="20"
        stroke="var(--color-primary-dark)"
        action-color="transparent"
      />
    </button>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { AccessPermissionsEnum } from '@/router/constants';
import { useRoute } from 'vue-router';
import NavMenuList from './NavMenuList.vue';
import adminMenu from '@/config/menu/admin';
import backOfficeMenu from '@/config/menu/backOffice';
import { useAuthService } from '@/modules/auth/auth.service';
import { useStore } from 'vuex';

const route = useRoute();
const store = useStore();
const isOpen = ref(true);

// Sections state for group toggles - теперь динамические
const sections = ref({});

const { checkHasPermission, getUserPermissions } = useAuthService();
const userPermissions = ref([]);

const updatePermissions = async () => {
  const permissionsMap = await getUserPermissions();
  userPermissions.value = Object.keys(permissionsMap || {}).filter((key) => permissionsMap[key]);
};

onMounted(updatePermissions);

const role = computed(() => store.getters.role);
watch(role, updatePermissions);

// Computed styles
const sidebarStyles = computed(() => ({
  width: isOpen.value ? '268px' : '100px',
  minWidth: isOpen.value ? '268px' : '100px',
  maxWidth: isOpen.value ? '268px' : '100px',
}));

const ListSideMenu = computed(() => {
  const isBackOfficeUser = checkHasPermission([AccessPermissionsEnum.CAN_READ_BACK_OFFICE]);
  return isBackOfficeUser ? backOfficeMenu : adminMenu;
});

// Methods
const toggleSidebar = () => {
  isOpen.value = !isOpen.value;
};

const toggleSection = (sectionName) => {
  sections.value[sectionName] = !sections.value[sectionName];
};

const handleItemClick = (data) => {
  console.log('Item clicked:', data);
  // Handle item click events if needed
};

const handleItemHover = (data) => {
  console.log('Item hover:', data);
  // Handle item hover events if needed
};

const handleListMenuClick = (item) => {
  console.log('List menu item clicked:', item);
  // Handle ListSideMenu item click events
};

onMounted(() => {
  // Initialize sections based on current route
  // Убираю хардкод инициализации секций в onMounted (строки 130-145)
});
</script>

<style lang="scss" scoped>
.left-container {
  &.is-closed {
    padding: 0px 20px;
  }

  // background-color: #f5f5f5;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  width: 268px;
  min-width: 268px;
  max-width: 268px;
  height: 100vh;
  padding: 0px 20px 0px 40px;
  transition: all 0.4s ease-in-out;
  z-index: 10;

  .logo-section {
    display: flex;
    justify-content: center;
    align-items: center;
    // padding: 26px 0 4px;
    height: 92px;
    width: 100%;

    .logo-wrapper {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .logo-compact {
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  .nav-section {
    display: flex;
    flex-direction: column;
    gap: 24px;
    flex: 1;
    overflow-y: hidden;

    .nav-item {
      // Legacy styles for backward compatibility
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0 16px;
      height: 42px;
      border-radius: 12px;
      transition: all 0.2s ease;
      cursor: pointer;

      &.main-item {
        margin-bottom: 8px;
      }

      &.active {
        background-color: rgba(14, 14, 34, 0.1);

        .nav-link {
          color: rgba(14, 14, 34, 0.8);
        }
      }

      a {
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      &.disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      .nav-link {
        display: flex;
        align-items: center;
        gap: 8px;
        width: 100%;
        text-decoration: none;
        color: rgba(14, 14, 34, 0.6);
        font-family: 'Montserrat', sans-serif;
        font-weight: 500;
        font-size: 16px;
        line-height: 1.2;
        transition: color 0.2s ease;

        &:hover {
          color: rgba(14, 14, 34, 0.8);
        }
      }

      .nav-text {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }

    .nav-group {
      display: flex;
      flex-direction: column;
      gap: 8px;

      .nav-group-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0;
        cursor: pointer;
        height: 18px;

        .group-title {
          font-family: 'Inter', sans-serif;
          font-weight: 700;
          font-size: 15px;
          line-height: 1.2;
          color: rgba(14, 14, 34, 0.6);
        }
      }

      // nav-group-items styles moved to NavGroupItems component
    }
  }

  .bottom-section {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 0 0 20px;
    width: 100%;

    .nav-item {
      // Legacy styles for backward compatibility
      display: flex;
      align-items: center;
      padding: 0 16px;
      height: 42px;
      border-radius: 12px;
      transition: all 0.2s ease;
      cursor: pointer;

      &.active {
        background-color: rgba(14, 14, 34, 0.1);

        .nav-link {
          color: rgba(14, 14, 34, 0.8);
        }
      }

      .nav-link {
        display: flex;
        align-items: center;
        gap: 8px;
        width: 100%;
        text-decoration: none;
        color: rgba(14, 14, 34, 0.6);
        font-family: 'Montserrat', sans-serif;
        font-weight: 500;
        font-size: 16px;
        line-height: 1.2;
        transition: color 0.2s ease;

        &:hover {
          color: rgba(14, 14, 34, 0.8);
        }
      }

      .nav-text {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
  }

  .toggle-button {
    position: absolute;
    right: 4px;
    top: 50%;
    transform: translateY(-50%);
    width: 36px;
    height: 36px;
    border-radius: 8px;
    border: none;
    background-color: rgba(14, 14, 34, 0);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
    z-index: 20;

    &:hover {
      background-color: rgba(14, 14, 34, 0.1);
    }

    .v-icon {
      color: rgba(14, 14, 34, 0.8);
    }
  }
}
</style>
